'use client';
import React, { useMemo } from 'react';
import { Typography, Container, Stack, Box, Grid } from '@mui/material';
import { DynamicHomePageSearchBanner } from '@peerless/data-access/strapi';
import { formatMediaUrl } from '../utils';
import { useGetDevice } from '../utils/useGetDevice';
import { QuickLink } from './QuickLink';
import { QuickSearchContainer } from '../layout/header/QuickSearchContainer';
import { QuickSearchWidget } from './QuickSearchWidget';
import { useTranslations } from 'next-intl';

interface Props {
  data: DynamicHomePageSearchBanner;
}

export const HomePageBanner: React.FC<Props> = ({ data }: Props) => {
  const t = useTranslations();
  const { title, background, tabletBackground, mobileBackground, quickLinks } = data;
  const device = useGetDevice();

  const backgroundImage = useMemo(() => {
    switch (device) {
      case 'desktop':
        return background;
      case 'tablet':
        return tabletBackground ?? background;
      case 'mobile':
        return mobileBackground ?? background;
      default:
        return background;
    }
  }, [background, device, mobileBackground, tabletBackground]);

  return (
    <Container
      maxWidth={false}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundImage: `url(${formatMediaUrl(backgroundImage)})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        minHeight: { xs: 'calc(100vh - 4rem)', md: 'calc(100vh - 6rem)' },
        paddingTop: { lg: '2rem', xl: '4rem' },
        paddingBottom: { lg: '2rem', xl: '4rem' },
      }}
    >
      <Stack justifyContent={'space-between'} alignItems={'center'} sx={{ flex: '1' }}>
        <Stack
          maxWidth={'lg'}
          spacing={2}
          sx={{ marginBottom: 4, width: '100%' }}
          justifyContent={'center'}
        >
          <Typography variant="h1" textAlign={'center'} color={'common.white'}>
            {title}
          </Typography>
          <Box sx={{ boxShadow: 8 }}>
            <QuickSearchContainer render={() => <QuickSearchWidget />} />
          </Box>
          <Typography
            color={'common.white'}
            fontStyle={'italic'}
            sx={{ textShadow: '0px 4px 4px rgba(0,0,0,1);', display: 'none' }}
          >
            {t('Search.search-helper-text')}
          </Typography>
        </Stack>
        <Grid maxWidth={'lg'} container spacing={2} justifyContent={'center'}>
          {quickLinks?.map(quickLink => (
            <Grid key={quickLink.id} item xs={6} lg={3}>
              <QuickLink image={quickLink.image} title={quickLink.title} url={quickLink.url} />
            </Grid>
          ))}
        </Grid>
      </Stack>
    </Container>
  );
};
